import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, X, Minus, ArrowRight, PencilSimple, Trash, CaretLeft, FloppyDisk,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";

/**
 * Manager-only Quota Templates manager. Three sub-views:
 *   list  -> browse existing templates, delete, open Apply / Edit
 *   edit  -> create / rename / add-remove quota lines
 *   apply -> pick engineers (all or subset), pick mode, POST /apply
 */
export default function TemplatesDialog({ open, engineers, onClose, onSaved }) {
  const [view, setView] = useState("list");           // 'list' | 'edit' | 'apply'
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);         // template being edited/applied

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/quota-templates");
      setTemplates(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setView("list"); setActive(null); load(); }
  }, [open]);

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/quota-templates/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const wipeAll = async () => {
    if (!window.confirm(
      "⚠ CLEAN SLATE\n\nThis clears EVERY technician's per-part quota AND deletes EVERY template.\n\nThe quota feature stays available — you can build fresh templates and apply them after.\n\nProceed?"
    )) return;
    try {
      const res = await api.post("/quota-templates/wipe-all");
      toast.success(
        `Cleared ${res.data.quotas_deleted} quota row(s) and ${res.data.templates_deleted} template(s). Fresh slate ready.`
      );
      load();
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            {view !== "list" && (
              <button
                type="button"
                onClick={() => { setView("list"); setActive(null); }}
                className="p-1 border border-neutral-300 hover:border-neutral-900"
                aria-label="Back"
                data-testid="templates-back"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
            )}
            Quota Templates
            {view === "edit" && (active ? ` — Edit "${active.name}"` : " — New template")}
            {view === "apply" && active && ` — Apply "${active.name}"`}
          </DialogTitle>
        </DialogHeader>

        {view === "list" && (
          <TemplatesList
            templates={templates}
            loading={loading}
            onNew={() => { setActive(null); setView("edit"); }}
            onEdit={(t) => { setActive(t); setView("edit"); }}
            onApply={(t) => { setActive(t); setView("apply"); }}
            onDelete={del}
            onWipe={wipeAll}
          />
        )}

        {view === "edit" && (
          <TemplateEditor
            editing={active}
            onCancel={() => { setView("list"); setActive(null); }}
            onSaved={() => { load(); setView("list"); setActive(null); onSaved && onSaved(); }}
          />
        )}

        {view === "apply" && active && (
          <TemplateApplier
            template={active}
            engineers={engineers}
            onCancel={() => { setView("list"); setActive(null); }}
            onApplied={() => { setView("list"); setActive(null); onSaved && onSaved(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


function TemplatesList({ templates, loading, onNew, onEdit, onApply, onDelete, onWipe }) {
  return (
    <div className="space-y-3">
      <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
        Templates are named quota loadouts (like <span className="font-mono">Standard Van Kit</span>). Create one, then apply it to any subset of technicians — or hit <b>Apply</b> and choose <span className="font-mono">All technicians</span> to roll it out to the whole team in one click. Mark one template as <b>Default</b> so it auto-applies to any technician you add later.
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-wider text-neutral-500">
          {templates.length} template{templates.length === 1 ? "" : "s"}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onWipe}
            variant="outline"
            data-testid="templates-wipe"
            className="rounded-none border-red-600 text-red-700 hover:bg-red-600 hover:text-white h-9"
          >
            Wipe all quotas
          </Button>
          <Button
            onClick={onNew}
            data-testid="templates-new"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 h-9"
          >
            <Plus size={14} weight="bold" className="mr-1" /> New template
          </Button>
        </div>
      </div>
      <div className="border border-neutral-200 bg-white">
        {loading ? (
          <div className="py-8 text-center text-neutral-400 text-xs font-mono">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="py-10 text-center text-neutral-400 text-xs">
            No templates yet. Click <b>New template</b> to create your first.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="templates-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-3 py-2 label-eyebrow">Name</th>
                <th className="px-3 py-2 label-eyebrow">Description</th>
                <th className="px-3 py-2 label-eyebrow text-right">Lines</th>
                <th className="px-3 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`template-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t.name}</span>
                      {t.is_default && (
                        <span
                          className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 border border-amber-400 text-amber-900 px-1 py-0.5"
                          title="Auto-applied to every newly added technician"
                          data-testid={`template-default-${t.id}`}
                        >
                          Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-600">{t.description || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right">{t.lines?.length || 0}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onApply(t)}
                        className="px-2 py-1 border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700 text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1"
                        data-testid={`template-apply-${t.id}`}
                      >
                        Apply <ArrowRight size={10} weight="bold" />
                      </button>
                      <button
                        onClick={() => onEdit(t)}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        data-testid={`template-edit-${t.id}`}
                        title="Edit"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => onDelete(t)}
                        className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                        data-testid={`template-delete-${t.id}`}
                        title="Delete"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function TemplateEditor({ editing, onCancel, onSaved }) {
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [isDefault, setIsDefault] = useState(!!editing?.is_default);
  const [lines, setLines] = useState(
    (editing?.lines && editing.lines.length > 0)
      ? editing.lines.map((l) => ({ ...l }))
      : [{ stock_code: null, name: "", group: null, uom: null, max_qty: 1 }],
  );
  const [busy, setBusy] = useState(false);

  const setLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const pickLine = (i, picked) => setLines(lines.map((l, idx) => idx === i ? {
    ...l, name: picked.name, stock_code: picked.stock_code || null,
    group: picked.group || null, uom: picked.uom || null,
  } : l));
  const addLine = () => setLines([...lines, { stock_code: null, name: "", group: null, uom: null, max_qty: 1 }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    const nm = name.trim();
    if (!nm) return toast.error("Give the template a name");
    const cleaned = lines
      .filter((l) => (l.name || "").trim() && Number(l.max_qty) > 0)
      .map((l) => ({
        stock_code: l.stock_code || null,
        name: l.name.trim(),
        group: l.group || null,
        uom: l.uom || null,
        max_qty: Math.max(1, parseInt(l.max_qty, 10) || 0),
      }));
    if (cleaned.length === 0) return toast.error("Add at least one quota line");
    setBusy(true);
    try {
      const body = { name: nm, description: description.trim() || null, lines: cleaned, is_default: !!isDefault };
      if (isEdit) await api.patch(`/quota-templates/${editing.id}`, body);
      else await api.post("/quota-templates", body);
      toast.success(isEdit ? "Template updated" : "Template created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1 space-y-1.5">
          <Label className="label-eyebrow">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Standard Van Kit"
            className="rounded-none border-neutral-400"
            data-testid="template-name"
          />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label className="label-eyebrow">Description (optional)</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this loadout is for"
            className="rounded-none border-neutral-400"
            data-testid="template-desc"
          />
        </div>
      </div>

      <div className="border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-3">
        <input
          id="tpl-is-default"
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="accent-neutral-900"
          data-testid="template-is-default"
        />
        <label htmlFor="tpl-is-default" className="text-xs text-amber-900 cursor-pointer select-none">
          <b>Default template</b> — auto-apply this loadout to every new technician added to the roster. Only one template can be default at a time; setting this will unset any other default.
        </label>
      </div>

      <div className="border border-neutral-200 bg-white">
        <table className="w-full text-sm" data-testid="template-lines">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-3 py-2 label-eyebrow">Part</th>
              <th className="px-3 py-2 label-eyebrow text-right">Max qty</th>
              <th className="px-3 py-2 label-eyebrow w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b border-neutral-100 last:border-0" data-testid={`tpl-line-${i}`}>
                <td className="px-3 py-2">
                  <StockPartCombobox
                    value={l}
                    onChange={(picked) => pickLine(i, picked)}
                    testid={`tpl-picker-${i}`}
                    placeholder="Search master list…"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center border border-neutral-400 h-8">
                    <button type="button" onClick={() => setLine(i, "max_qty", Math.max(1, (parseInt(l.max_qty, 10) || 0) - 1))}
                      className="w-7 h-full flex items-center justify-center border-r border-neutral-400 hover:bg-neutral-900 hover:text-white disabled:opacity-40"
                      disabled={(parseInt(l.max_qty, 10) || 0) <= 1}
                      data-testid={`tpl-dec-${i}`}
                      aria-label="Decrease">
                      <Minus size={12} weight="bold" />
                    </button>
                    <Input type="number" min={1} value={l.max_qty}
                      onChange={(e) => setLine(i, "max_qty", e.target.value)}
                      className="w-14 h-full border-0 rounded-none font-mono text-right focus-visible:ring-0"
                      data-testid={`tpl-max-${i}`} />
                    <button type="button" onClick={() => setLine(i, "max_qty", (parseInt(l.max_qty, 10) || 0) + 1)}
                      className="w-7 h-full flex items-center justify-center border-l border-neutral-400 hover:bg-neutral-900 hover:text-white"
                      data-testid={`tpl-inc-${i}`}
                      aria-label="Increase">
                      <Plus size={12} weight="bold" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => removeLine(i)}
                    className="h-8 w-8 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600"
                    data-testid={`tpl-remove-${i}`}
                    aria-label="Remove">
                    <X size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-neutral-200 px-3 py-2">
          <button type="button" onClick={addLine}
            className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500 flex items-center gap-1"
            data-testid="tpl-add-line">
            <Plus size={12} weight="bold" /> Add part
          </button>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="template-save"
          className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
          <FloppyDisk size={14} weight="bold" className="mr-1" />
          {busy ? "Saving…" : isEdit ? "Save template" : "Create template"}
        </Button>
      </DialogFooter>
    </div>
  );
}


function TemplateApplier({ template, engineers, onCancel, onApplied }) {
  const [scope, setScope] = useState("all");
  const [mode, setMode] = useState("merge");
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = (engineers || []).filter((e) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return e.name.toLowerCase().includes(q)
      || (e.employee_id || "").toLowerCase().includes(q)
      || (e.region || "").toLowerCase().includes(q);
  });
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (filtered.every((e) => selected[e.id])) {
      const next = { ...selected };
      filtered.forEach((e) => { delete next[e.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach((e) => { next[e.id] = true; });
      setSelected(next);
    }
  };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const submit = async () => {
    if (scope === "select" && selectedCount === 0) return toast.error("Pick at least one engineer or switch to All");
    if (mode === "replace" && !window.confirm(
      `REPLACE mode will delete ANY existing quotas for the selected engineers and set them to only what's in "${template.name}". Continue?`,
    )) return;
    setBusy(true);
    try {
      const body = { mode };
      if (scope === "select") {
        body.apply_to = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      }
      const res = await api.post(`/quota-templates/${template.id}/apply`, body);
      const { engineers: n = 0, created = 0, updated = 0, deleted = 0 } = res.data;
      toast.success(`Applied to ${n} — ${created} added / ${updated} updated${deleted ? ` / ${deleted} removed` : ""}`);
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
        <b>{template.name}</b> ({template.lines?.length || 0} line{template.lines?.length === 1 ? "" : "s"})
        {template.description && <span className="opacity-70"> — {template.description}</span>}
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Mode</Label>
        <div className="flex border border-neutral-300" data-testid="tpl-mode">
          <button type="button" onClick={() => setMode("merge")}
            className={`h-9 px-3 text-xs font-mono uppercase tracking-wider border-r border-neutral-300 flex-1 ${
              mode === "merge" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
            }`}
            data-testid="tpl-mode-merge">
            Merge (keep other caps)
          </button>
          <button type="button" onClick={() => setMode("replace")}
            className={`h-9 px-3 text-xs font-mono uppercase tracking-wider flex-1 ${
              mode === "replace" ? "bg-red-900 text-white" : "bg-white hover:bg-neutral-50"
            }`}
            data-testid="tpl-mode-replace">
            Replace (wipe existing)
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Apply to</Label>
        <div className="flex border border-neutral-300" data-testid="tpl-scope">
          <button type="button" onClick={() => setScope("all")}
            className={`h-9 px-3 text-xs font-mono uppercase tracking-wider border-r border-neutral-300 flex-1 ${scope === "all" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
            data-testid="tpl-scope-all">
            All field staff ({(engineers || []).length})
          </button>
          <button type="button" onClick={() => setScope("select")}
            className={`h-9 px-3 text-xs font-mono uppercase tracking-wider flex-1 ${scope === "select" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
            data-testid="tpl-scope-select">
            Select engineers{selectedCount ? ` · ${selectedCount}` : ""}
          </button>
        </div>

        {scope === "select" && (
          <div className="border border-neutral-200">
            <div className="border-b border-neutral-200 px-3 py-2 flex items-center gap-2">
              <Input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)}
                className="h-8 rounded-none border-neutral-300" data-testid="tpl-filter" />
              <button type="button" onClick={toggleAll}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 hover:bg-neutral-900 hover:text-white whitespace-nowrap"
                data-testid="tpl-toggle-all">
                Toggle all
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.map((e) => (
                <label key={e.id}
                  className="flex items-center gap-2 px-3 py-1.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer text-sm"
                  data-testid={`tpl-eng-${e.id}`}>
                  <input type="checkbox" checked={!!selected[e.id]} onChange={() => toggle(e.id)}
                    className="accent-neutral-900" />
                  <span className="flex-1">{e.name}</span>
                  <span className="font-mono text-[10px] text-neutral-500">{e.employee_id || ""}</span>
                  <span className="text-[10px] text-neutral-400">{e.region || ""}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="tpl-apply-submit"
          className={`rounded-none text-white ${mode === "replace" ? "bg-red-900 hover:bg-red-800" : "bg-neutral-900 hover:bg-neutral-800"}`}>
          {busy ? "Applying…" : scope === "all"
            ? `${mode === "replace" ? "Replace on" : "Merge into"} all ${(engineers || []).length}`
            : `${mode === "replace" ? "Replace on" : "Merge into"} ${selectedCount || 0} selected`}
        </Button>
      </DialogFooter>
    </div>
  );
}
